import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Send, Upload, CheckCircle2, FileText, Loader2, AlertTriangle } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export const PracticalAssessment = ({ readonly }) => {
  const [state, setState] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/rm/training/practical").then((r) => {
    setState(r.data);
    if (r.data.personal_email) setEmail(r.data.personal_email);
  }).catch(() => setState({}));
  useEffect(() => { load(); }, []);

  const notify = async () => {
    setBusy("notify");
    try {
      const { data } = await api.post("/rm/training/practical/notify", { personal_email: email });
      toast.success(data.email_sent ? "Management notified — credits will be allocated shortly" : "Saved, but the email failed — please tell management directly");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't notify management"); }
    setBusy("");
  };

  const submit = async () => {
    if (!file) return;
    setBusy("upload");
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/rm/training/practical/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy("");
  };

  if (!state) return <p className="mt-8 text-sm text-slate-400">Loading…</p>;
  const resubmit = !!state.resubmit_requested;
  const done = !!state.submission && !resubmit;

  return (
    <div className="mt-8 space-y-5 border-t border-slate-100 pt-6" data-testid="rm-practical-panel">
      {readonly && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="rm-practical-readonly-banner">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
          <p>You're viewing as admin (read-only) — the fields below are locked. Log in with your RM account to notify management or submit your report.</p>
        </div>
      )}
      {resubmit && (
        <div className="rounded-2xl border border-orange-200 bg-orange-50 p-5" data-testid="rm-practical-resubmit-card">
          <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-orange-700">
            <AlertTriangle size={14} /> Action needed — re-download &amp; resubmit your report
          </p>
          <p className="mt-1 text-sm font-bold text-slate-900">Management reviewed your Capital Readiness Report and asked for changes.</p>
          <div className="mt-3 rounded-xl border border-orange-200 bg-white p-4">
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">What you need to do</p>
            <p className="mt-1 whitespace-pre-line text-sm text-slate-800" data-testid="rm-practical-review-notes">{state.review?.notes}</p>
            <p className="mt-2 text-[11px] text-slate-400">From {state.review?.by} · {fmt(state.review?.at)}</p>
          </div>
          <p className="mt-3 text-xs text-slate-600">
            Make the changes on your founder account, <b>re-download your Capital Readiness Report</b>, then upload the new PDF in Step 3 below.
            {state.submission?.filename && <> Previous submission: <span className="font-semibold">{state.submission.filename}</span> ({fmt(state.submission.submitted_at)}).</>}
          </p>
        </div>
      )}
      {done ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="rm-practical-congrats">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
          <p className="mt-3 text-base font-extrabold text-emerald-900 md:text-lg">Congratulations on completing the Clinq RM Training Programme.</p>
          <p className="mt-2 text-sm text-emerald-800">Your results are now being reviewed by management and we will be in touch shortly to let you know whether you are ready to be onboarded to the Clinq team.</p>
          <p className="mt-4 inline-flex items-center gap-2 rounded-lg bg-white px-3 py-1.5 text-xs text-slate-600">
            <FileText size={13} /> {state.submission.filename} · submitted {fmt(state.submission.submitted_at)}
          </p>
          {state.rm_status === "approved" && (
            <p className="mt-3 text-sm font-bold text-[#0A8B85]" data-testid="rm-practical-approved">You have been approved — welcome to the Clinq team.</p>
          )}
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="rm-practical-notify-card">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#0A8B85]">Steps 1 &amp; 2 — Credits</p>
            <p className="mt-1 text-sm font-bold text-slate-900">Notify management so credits can be allocated</p>
            <p className="mt-1 text-xs text-slate-500">Enter the personal email address you registered on prep.clinq.africa (and will use on clinq.africa). Use the same button again if you need a top-up for Step 2.</p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your.name@gmail.com" disabled={readonly}
                className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0A8B85]"
                data-testid="rm-practical-email" />
              <button onClick={notify} disabled={readonly || busy === "notify" || !email.includes("@")}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#0A8B85] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#087a74] disabled:opacity-50"
                data-testid="rm-practical-notify-btn">
                {busy === "notify" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Notify Management — I'm Ready to Start
              </button>
            </div>
            {state.notified_at?.length > 0 && (
              <p className="mt-2 text-[11px] text-emerald-700" data-testid="rm-practical-notified-note">
                Management notified {state.notified_at.length}× · last {fmt(state.notified_at[state.notified_at.length - 1])}
              </p>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="rm-practical-upload-card">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#0A8B85]">Step 3 — Submit</p>
            <p className="mt-1 text-sm font-bold text-slate-900">{resubmit ? "Upload your updated Capital Readiness Report (PDF)" : "Upload Your Capital Readiness Report (PDF)"}</p>
            <p className="mt-1 text-xs text-slate-500">{resubmit ? "Your resubmission goes straight back to management for review." : "This completes your practical assessment and sends your report to management for review."}</p>
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="rm-practical-file-input" />
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
              <button onClick={() => fileRef.current?.click()} disabled={readonly}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                data-testid="rm-practical-choose-btn">
                <FileText size={15} /> {file ? file.name : "Choose PDF"}
              </button>
              <button onClick={submit} disabled={readonly || !file || busy === "upload"}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#0A2540] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#0c2f4d] disabled:opacity-50"
                data-testid="rm-practical-upload-btn">
                {busy === "upload" ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {resubmit ? "Resubmit my report" : "Submit my report"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
